import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { euro, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Modal, Field, inputCls, Badge, EmptyState, Toolbar } from "@/components/kit";
import { Plus, Trash2, FileText, Pencil, CreditCard, X, Download, Send, ArrowRightLeft, CheckCircle2, BellRing } from "lucide-react";

const EMPTY = {
  type: "facture",
  client_id: "",
  client_name: "",
  status: "brouillon",
  issue_date: new Date().toISOString().slice(0, 10),
  due_date: "",
  items: [{ description: "", quantity: 1, unit_price: 0 }],
  tax_rate: 20,
  notes: "",
};

export default function Invoices() {
  const { user } = useAuth();
  const [tab, setTab] = useState("facture");
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [payingId, setPayingId] = useState(null);

  const load = () => {
    api.get(`/invoices?type=${tab}`).then((r) => setList(r.data)).catch(() => {});
  };
  useEffect(load, [tab]);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  const totals = useMemo(() => {
    const subtotal = form.items.reduce((s, i) => s + Number(i.quantity || 0) * Number(i.unit_price || 0), 0);
    const tax = (subtotal * Number(form.tax_rate || 0)) / 100;
    return { subtotal, tax, total: subtotal + tax };
  }, [form.items, form.tax_rate]);

  const openNew = () => {
    setForm({ ...EMPTY, type: tab, items: [{ description: "", quantity: 1, unit_price: 0 }] });
    setEditId(null);
    setOpen(true);
  };
  const openEdit = (inv) => {
    setForm({
      type: inv.type,
      client_id: inv.client_id || "",
      client_name: inv.client_name || "",
      status: inv.status,
      issue_date: inv.issue_date || "",
      due_date: inv.due_date || "",
      items: inv.items?.length ? inv.items : [{ description: "", quantity: 1, unit_price: 0 }],
      tax_rate: inv.tax_rate ?? 20,
      notes: inv.notes || "",
    });
    setEditId(inv.id);
    setOpen(true);
  };

  const setItem = (idx, key, val) => {
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)) }));
  };
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { description: "", quantity: 1, unit_price: 0 }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      items: form.items.map((i) => ({
        description: i.description,
        quantity: Number(i.quantity),
        unit_price: Number(i.unit_price),
      })),
      tax_rate: Number(form.tax_rate),
    };
    try {
      if (editId) await api.put(`/invoices/${editId}`, payload);
      else await api.post("/invoices", payload);
      toast.success(editId ? "Document mis à jour" : "Document créé");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Supprimé");
      load();
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    }
  };

  const pay = async (inv) => {
    setPayingId(inv.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        invoice_id: inv.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
      setPayingId(null);
    }
  };

  const [busyId, setBusyId] = useState(null);

  const downloadPdf = async (inv) => {
    setBusyId(inv.id + "-pdf");
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Impossible de générer le PDF");
    } finally {
      setBusyId(null);
    }
  };

  const sendEmail = async (inv) => {
    setBusyId(inv.id + "-mail");
    try {
      const { data } = await api.post(`/invoices/${inv.id}/send`);
      toast.success(`Envoyé à ${data.sent_to}`);
      load();
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const convert = async (inv) => {
    if (!window.confirm(`Convertir le devis ${inv.number} en facture ?`)) return;
    setBusyId(inv.id + "-conv");
    try {
      const { data } = await api.post(`/invoices/${inv.id}/convert`);
      toast.success(`Facture ${data.number} créée`);
      load();
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="animate-fade-up" data-testid="invoices-page">
      <Toolbar>
        <div className="flex border border-border">
          {[
            { k: "facture", l: "Factures" },
            { k: "devis", l: "Devis" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`tab-${t.k}`}
              className={`px-5 py-2 text-sm font-medium transition-colors ${
                tab === t.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>
        <Btn onClick={openNew} data-testid="new-invoice-btn">
          <Plus size={16} /> {tab === "facture" ? "Nouvelle facture" : "Nouveau devis"}
        </Btn>
      </Toolbar>

      {list.length === 0 ? (
        <EmptyState
          icon={FileText}
          title={`Aucun ${tab === "facture" ? "facture" : "devis"} pour l'instant`}
          sub="Créez votre premier document avec des lignes détaillées, TVA et paiement en ligne."
          action={<Btn onClick={openNew}><Plus size={16} /> Créer</Btn>}
        />
      ) : (
        <div className="border border-border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3 font-medium">Numéro</th>
                <th className="px-4 py-3 font-medium">Client</th>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Statut</th>
                <th className="px-4 py-3 font-medium">Suivi</th>
                <th className="px-4 py-3 text-right font-medium">Total TTC</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((inv) => (
                <tr key={inv.id} className="border-b border-border transition-colors last:border-0 hover:bg-accent/40" data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{inv.number}</td>
                  <td className="px-4 py-3">{inv.client_name || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{inv.issue_date || "—"}</td>
                  <td className="px-4 py-3"><Badge>{inv.status}</Badge></td>
                  <td className="px-4 py-3" data-testid={`tracking-${inv.id}`}>
                    <div className="flex flex-col gap-1 text-xs">
                      {inv.sent_at ? (
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Send size={11} /> Envoyée le {new Date(inv.sent_at).toLocaleDateString("fr-FR")}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/50">Non envoyée</span>
                      )}
                      {inv.payment_status === "paid" && (
                        <span className="flex items-center gap-1 font-medium text-emerald-400">
                          <CheckCircle2 size={11} /> Payée en ligne
                        </span>
                      )}
                      {inv.reminder_count > 0 && inv.payment_status !== "paid" && (
                        <span className="flex items-center gap-1 text-primary">
                          <BellRing size={11} /> {inv.reminder_count} relance{inv.reminder_count > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{euro(inv.total)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {inv.type === "devis" && !inv.converted_invoice_id && (
                        <button
                          onClick={() => convert(inv)}
                          disabled={busyId === inv.id + "-conv"}
                          data-testid={`convert-btn-${inv.id}`}
                          title="Convertir en facture"
                          className="p-2 text-primary transition-colors hover:bg-primary/10 disabled:opacity-50"
                        >
                          <ArrowRightLeft size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => downloadPdf(inv)}
                        disabled={busyId === inv.id + "-pdf"}
                        data-testid={`pdf-btn-${inv.id}`}
                        title="Télécharger le PDF"
                        className="p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
                      >
                        <Download size={16} />
                      </button>
                      <button
                        onClick={() => sendEmail(inv)}
                        disabled={busyId === inv.id + "-mail"}
                        data-testid={`send-btn-${inv.id}`}
                        title="Envoyer par e-mail au client"
                        className="p-2 text-blue-400 transition-colors hover:bg-blue-500/10 disabled:opacity-50"
                      >
                        <Send size={16} />
                      </button>
                      {inv.type === "facture" && inv.status !== "payée" && (
                        <button
                          onClick={() => pay(inv)}
                          disabled={payingId === inv.id}
                          data-testid={`pay-btn-${inv.id}`}
                          title="Payer en ligne"
                          className="p-2 text-emerald-400 transition-colors hover:bg-emerald-500/10 disabled:opacity-50"
                        >
                          <CreditCard size={16} />
                        </button>
                      )}
                      <button onClick={() => openEdit(inv)} data-testid={`edit-btn-${inv.id}`} title="Modifier" className="p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
                        <Pencil size={16} />
                      </button>
                      {user?.role === "admin" && (
                        <button onClick={() => del(inv.id)} data-testid={`delete-btn-${inv.id}`} title="Supprimer" className="p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Modifier le document" : `Nouveau ${form.type}`} wide>
        <form onSubmit={save} className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Type">
              <select className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="invoice-type">
                <option value="facture">Facture</option>
                <option value="devis">Devis</option>
              </select>
            </Field>
            <Field label="Client">
              <select
                className={inputCls}
                value={form.client_id}
                onChange={(e) => {
                  const c = clients.find((x) => x.id === e.target.value);
                  setForm({ ...form, client_id: e.target.value, client_name: c ? (c.company || c.name) : "" });
                }}
                data-testid="invoice-client"
              >
                <option value="">— Sélectionner —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.company || c.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Statut">
              <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="invoice-status">
                <option value="brouillon">Brouillon</option>
                <option value="envoyée">Envoyée</option>
                <option value="payée">Payée</option>
                <option value="annulée">Annulée</option>
              </select>
            </Field>
            <Field label="Date d'émission">
              <input type="date" className={inputCls} value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} data-testid="invoice-issue-date" />
            </Field>
            <Field label="Échéance">
              <input type="date" className={inputCls} value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="invoice-due-date" />
            </Field>
            <Field label="TVA (%)">
              <input type="number" step="0.1" className={inputCls} value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} data-testid="invoice-tax" />
            </Field>
          </div>

          {/* Line items */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Lignes</label>
              <button type="button" onClick={addItem} data-testid="add-item-btn" className="flex items-center gap-1 text-sm text-primary hover:underline">
                <Plus size={14} /> Ajouter
              </button>
            </div>
            <div className="space-y-2">
              {form.items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2">
                  <input placeholder="Description" className={`${inputCls} col-span-6`} value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} data-testid={`item-desc-${idx}`} />
                  <input type="number" step="1" placeholder="Qté" className={`${inputCls} col-span-2`} value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} data-testid={`item-qty-${idx}`} />
                  <input type="number" step="0.01" placeholder="Prix U." className={`${inputCls} col-span-3`} value={it.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} data-testid={`item-price-${idx}`} />
                  <button type="button" onClick={() => removeItem(idx)} className="col-span-1 flex items-center justify-center text-muted-foreground hover:text-destructive" data-testid={`item-remove-${idx}`}>
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <div className="ml-auto max-w-xs space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Sous-total</span><span className="font-mono">{euro(totals.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">TVA ({form.tax_rate}%)</span><span className="font-mono">{euro(totals.tax)}</span></div>
              <div className="flex justify-between border-t border-border pt-1 font-display text-base font-bold"><span>Total TTC</span><span className="font-mono text-primary" data-testid="invoice-total">{euro(totals.total)}</span></div>
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Annuler</Btn>
            <Btn type="submit" loading={saving} data-testid="save-invoice-btn">Enregistrer</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
